/**
 * Dados cadastrais do cliente final (#1014) — o espelho do contrato do backend
 * (`app/schemas/dados_cadastrais.py`): listas de porte/setor e a validação de CPF/CNPJ
 * pelo dígito verificador. A máscara da tela não é validação — quem decide é o backend —,
 * mas conferir aqui evita a ida ao servidor para um número digitado errado.
 */

export const PORTES = [
  { value: 'micro', label: 'Microempresa (até 9 funcionários)' },
  { value: 'small', label: 'Pequena (10-49 funcionários)' },
  { value: 'medium', label: 'Média (50-249 funcionários)' },
  { value: 'large', label: 'Grande (250+ funcionários)' },
] as const

export const SETORES = [
  { value: 'technology', label: 'Tecnologia' },
  { value: 'finance', label: 'Financeiro' },
  { value: 'healthcare', label: 'Saúde' },
  { value: 'education', label: 'Educação' },
  { value: 'retail', label: 'Varejo' },
  { value: 'manufacturing', label: 'Indústria' },
  { value: 'services', label: 'Serviços' },
  { value: 'other', label: 'Outros' },
] as const

export type Porte = (typeof PORTES)[number]['value']
export type Setor = (typeof SETORES)[number]['value']

/** Telefone e CEP são só dígitos. Documento NÃO: use `normalizeDocument`. */
export const onlyDigits = (value: string | null | undefined): string =>
  (value ?? '').replace(/\D/g, '')

/**
 * CPF/CNPJ sem máscara e em MAIÚSCULAS — a forma que o backend grava e compara. O CNPJ é
 * alfanumérico desde julho/2026 (IN RFB 2.229/2024): letras nas 12 primeiras posições, DV
 * numérico; `\D` recusaria (ou, pior, mutilaria) o documento de toda empresa nova.
 */
export const normalizeDocument = (value: string | null | undefined): string =>
  (value ?? '').toUpperCase().replace(/[^0-9A-Z]/g, '')

// O valor de cada posição no módulo 11 é o código ASCII menos 48 ('0'..'9' → 0..9,
// 'A'..'Z' → 17..42); para o documento só de dígitos é a conta de sempre.
const dv = (chars: string, pesos: number[]): number => {
  const soma = chars.split('').reduce((acc, c, i) => acc + (c.charCodeAt(0) - 48) * pesos[i], 0)
  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

const cpfValido = (d: string): boolean => {
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false
  const dv1 = dv(d.slice(0, 9), [10, 9, 8, 7, 6, 5, 4, 3, 2])
  const dv2 = dv(d.slice(0, 10), [11, 10, 9, 8, 7, 6, 5, 4, 3, 2])
  return d.slice(9) === `${dv1}${dv2}`
}

const cnpjValido = (d: string): boolean => {
  if (!/^[0-9A-Z]{12}\d{2}$/.test(d) || /^(\d)\1{13}$/.test(d)) return false
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
  const pesos2 = [6, ...pesos1]
  const dv1 = dv(d.slice(0, 12), pesos1)
  const dv2 = dv(d.slice(0, 13), pesos2)
  return d.slice(12) === `${dv1}${dv2}`
}

/** `'cpf' | 'cnpj'` quando o documento é válido (dígito verificador), senão `null`. */
export const tipoDeDocumentoValido = (value: string | null | undefined): 'cpf' | 'cnpj' | null => {
  const d = normalizeDocument(value)
  if (d.length === 11) return cpfValido(d) ? 'cpf' : null
  if (d.length === 14) return cnpjValido(d) ? 'cnpj' : null
  return null
}

export const isValidCpfCnpj = (value: string | null | undefined): boolean =>
  tipoDeDocumentoValido(value) !== null

/** `00.000.000/0000-00` (alfanumérico incluído) ou `000.000.000-00`; qualquer outro tamanho volta como está. */
export const formatDocument = (value: string | null | undefined): string => {
  const d = normalizeDocument(value)
  if (d.length === 14) return d.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, '$1.$2.$3/$4-$5')
  if (d.length === 11 && /^\d+$/.test(d))
    return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  return value ?? ''
}

export const formatCep = (value: string | null | undefined): string => {
  const d = onlyDigits(value)
  return d.length === 8 ? d.replace(/^(\d{5})(\d{3})$/, '$1-$2') : (value ?? '')
}

export const formatPhone = (value: string | null | undefined): string => {
  const d = onlyDigits(value)
  if (d.length === 11) return d.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
  if (d.length === 10) return d.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')
  return value ?? ''
}

export const isValidPhone = (value: string | null | undefined): boolean => {
  const n = onlyDigits(value).length
  return n === 10 || n === 11
}
